/**
 * Data Hub — Import & Run Workflow, Step 1.
 *
 * Pick a file, optionally preview/edit rows in the same dialog, then hand off
 * to the page. Columns → Promote still run in `RunConsole` on the Data Hub page
 * (KPI-Config-style full lower-pane swap), never as a stepper inside this modal.
 *
 * Both phase groups always run — shown as info only, not as toggles.
 */
import { useEffect, useId, useRef, useState } from "react";

import {
  ArrowLeft,
  Database,
  FileSpreadsheet,
  FlaskConical,
  Pencil,
} from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  openDataHubLedgerRun,
  persistDataHubLedgerStep,
  uploadDataHubStoredFile,
} from "../../Store/Reducers/DataHubConsoleSlice";
import { describeError } from "../data/api";
import { DATASET_LABELS, type DatasetKind } from "../data/dataset";
import { useActiveDataset } from "../hooks/useActiveDataset";
import { parseSheet, type SheetTable } from "../lib/sheet-to-csv";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hands the parsed file to the page, which drives every remaining step. */
  onStart: (payload: {
    file: File;
    table: SheetTable;
    dataset: DatasetKind;
    ledgerRunId?: string;
  }) => void;
  /**
   * When set as the dialog opens, restore this file on the preview step
   * (Back from Columns in the run console).
   */
  seed?: {
    file: File;
    table: SheetTable;
    dataset: DatasetKind;
  } | null;
};

function formatHeaderLabel(header: string) {
  return header.replace(/_/g, " ").trim() || header;
}

export function RunWizard({ open, onOpenChange, onStart, seed = null }: Props) {
  const dispatch = useAppDispatch();
  const [file, setFile] = useState<File | null>(null);
  const [table, setTable] = useState<SheetTable | null>(null);
  const [sheetNote, setSheetNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<string | null>(null);
  // Dataset is controlled at Data Hub level (Synthetic data toggle). This
  // dialog only shows which lane the file will land in — no per-file override.
  // Seed restores the dataset of a run the user backed out of; otherwise follow
  // the live high-level toggle.
  const [activeDataset, selectDataset] = useActiveDataset();
  const dataset: DatasetKind = seed?.dataset ?? activeDataset;
  const [retained, setRetained] = useState<{
    storage_path: string;
    file_hash: string | null;
  } | null>(null);
  const fileInputId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Re-sync whenever the dialog opens. A seed restores the preview after Back
  // from Columns; otherwise start from a clean pick-file state.
  useEffect(() => {
    if (!open) return;
    if (seed) {
      setFile(seed.file);
      setTable(seed.table);
      setSheetNote(null);
      setError(null);
      setRetained(null);
      setShowPreview(true);
      setEditingCell(null);
      setHoveredCell(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(null);
    setTable(null);
    setSheetNote(null);
    setError(null);
    setRetained(null);
    setShowPreview(false);
    setEditingCell(null);
    setHoveredCell(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open, seed]);

  const reset = () => {
    setFile(null);
    setTable(null);
    setSheetNote(null);
    setError(null);
    setRetained(null);
    setShowPreview(false);
    setEditingCell(null);
    setHoveredCell(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onPick = async (picked: File | null) => {
    setError(null);
    setTable(null);
    setSheetNote(null);
    setRetained(null);
    setShowPreview(false);
    setEditingCell(null);
    setHoveredCell(null);
    setFile(picked);
    if (!picked) return;
    setBusy(true);
    try {
      const parsed = await parseSheet(picked);
      setTable(parsed);
      const size = `${parsed.rows.length.toLocaleString()} rows · ${parsed.headers.length} columns`;
      setSheetNote(
        parsed.all_sheet_names.length > 1
          ? `${size} — ${parsed.all_sheet_names.length} sheets, using "${parsed.sheet_name}"`
          : size
      );
    } catch (err: any) {
      setError(describeError(err));
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    reset();
    onOpenChange(false);
  };

  const backToUpload = () => {
    if (busy) return;
    setShowPreview(false);
    setEditingCell(null);
    setHoveredCell(null);
  };

  const updateCell = (rowIndex: number, header: string, value: string) => {
    setTable(prev => {
      if (!prev) return prev;
      const rows = prev.rows.map((row, i) =>
        i === rowIndex ? { ...row, [header]: value } : row
      );
      return { ...prev, rows };
    });
  };

  const cont = async () => {
    if (!file || !table || busy) return;
    // Lock the console to the Data Hub–level dataset before the run starts.
    selectDataset(dataset);
    setBusy(true);
    setError(null);
    try {
      let storagePath = retained?.storage_path ?? null;
      let fileHash = retained?.file_hash ?? null;
      if (!storagePath) {
        try {
          const uploaded = await dispatch(
            uploadDataHubStoredFile(file)
          ).unwrap();
          if (uploaded) {
            storagePath = uploaded.storage_path;
            fileHash = uploaded.file_hash;
          }
        } catch {
          // No store or a failed upload: resume-before-Import will ask to
          // re-select the file rather than blocking this one.
        }
      }
      let ledgerRunId: string | undefined;
      try {
        const ledger = await dispatch(
          openDataHubLedgerRun({
            source_filename: file.name,
            storage_path: storagePath,
            file_hash: fileHash,
            sheet_name: table.sheet_name,
            row_count: table.rows.length,
            dataset,
          })
        ).unwrap();
        ledgerRunId = ledger.id;
        await dispatch(
          persistDataHubLedgerStep({
            runId: ledger.id,
            step: "file",
            decisions: {},
            close: {
              outcome_counts: {
                rows: table.rows.length,
                columns: table.headers.length,
              },
            },
          })
        ).unwrap();
      } catch {
        // Degrade: the wizard still starts; resume will not be available.
      }
      onStart({ file, table, dataset, ledgerRunId });
      reset();
      onOpenChange(false);
    } catch (err: any) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const datasetLabel = DATASET_LABELS[dataset];
  const cellKey = (rowIndex: number, header: string) => `${rowIndex}:${header}`;

  const renderEditableCell = (
    rowIndex: number,
    header: string,
    value: string
  ) => {
    const key = cellKey(rowIndex, header);
    const isEditing = editingCell === key;
    const isHovered = hoveredCell === key;
    return (
      <td
        key={header}
        className={`
          relative min-w-[150px] cursor-pointer border-none px-4 py-3 pr-10 text-sm text-gray-700 transition-colors
          ${isEditing ? "bg-violet-50 p-2" : ""}
          ${!isEditing ? "hover:bg-violet-50" : ""}
        `.trim()}
        onMouseEnter={() => setHoveredCell(key)}
        onMouseLeave={() => setHoveredCell(null)}
        onClick={() => {
          if (!isEditing) setEditingCell(key);
        }}
        title="Click to edit"
      >
        {isEditing ? (
          <input
            type="text"
            defaultValue={value}
            autoFocus
            className="w-full rounded border-2 border-violet-500 bg-violet-50 px-2 py-1.5 text-sm text-gray-800 outline-none shadow-[0_0_0_3px_rgba(139,92,246,0.1)]"
            onBlur={e => {
              updateCell(rowIndex, header, e.target.value);
              setEditingCell(null);
            }}
            onKeyDown={e => {
              if (e.key === "Enter") {
                (e.target as HTMLInputElement).blur();
              } else if (e.key === "Escape") {
                setEditingCell(null);
              }
            }}
          />
        ) : (
          <>
            <span className="inline-block w-full pr-2">{value || "—"}</span>
            {isHovered && (
              <button
                type="button"
                className="absolute right-3 top-1/2 z-[100] flex -translate-y-1/2 items-center justify-center rounded bg-violet-500 p-1.5 text-white shadow-sm transition-all hover:bg-violet-600 hover:shadow"
                onClick={e => {
                  e.stopPropagation();
                  setEditingCell(key);
                }}
                title="Click to edit"
              >
                <Pencil size={14} />
              </button>
            )}
          </>
        )}
      </td>
    );
  };

  return (
    <CommonModal
      isOpen={open}
      toggle={close}
      size={showPreview ? "screen-2xl" : "4xl"}
      usePortal
      className={showPreview ? "!max-h-[95vh]" : undefined}
      containerClassName={showPreview ? "!p-3 sm:!p-4" : undefined}
      title={
        showPreview
          ? "Import & Run Workflow — Step 1: Pick file (Preview)"
          : "Import & Run Workflow — Step 1: Pick file"
      }
      subtitle={
        showPreview
          ? `${table?.rows.length.toLocaleString() ?? 0} rows · ${DATASET_LABELS[dataset]} data — review and edit before continuing.`
          : "Choose a CSV file to preview, then continue on the next steps."
      }
      footer={
        showPreview ? (
          <>
            <Button
              type="button"
              variant="outline-primary"
              onClick={backToUpload}
              disabled={busy}
              icon={<ArrowLeft className="h-4 w-4" />}
            >
              Back to Upload
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={cont}
              disabled={!table || busy}
              loading={busy}
            >
              Continue to run workflow
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline-primary"
              onClick={close}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={() => setShowPreview(true)}
              disabled={!table || busy}
            >
              Preview
            </Button>
          </>
        )
      }
    >
      {showPreview && table ? (
        <div className="flex min-h-0 flex-col space-y-3 px-1 py-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="m-0 text-sm text-gray-500">
              Hover a cell and click the edit icon to change values. Changes
              apply to this run only.
            </p>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${
                dataset === "synthetic"
                  ? "bg-amber-50 text-amber-900 ring-amber-200"
                  : "bg-violet-50 text-violet-900 ring-violet-200"
              }`}
              title={`This preview will import as ${DATASET_LABELS[dataset]} data`}
            >
              {dataset === "synthetic" ? (
                <FlaskConical className="h-3.5 w-3.5" />
              ) : (
                <Database className="h-3.5 w-3.5" />
              )}
              {DATASET_LABELS[dataset]} data
            </span>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="max-h-[min(70vh,720px)] overflow-auto">
              <table className="min-w-[1200px] w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    {table.headers.map(header => (
                      <th
                        key={header}
                        className="border-b border-gray-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600"
                      >
                        {formatHeaderLabel(header)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {table.rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-gray-100 transition-colors last:border-b-0"
                    >
                      {table.headers.map(header =>
                        renderEditableCell(rowIndex, header, row[header] ?? "")
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6 px-1 py-1">
          <div className="space-y-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              File
            </p>
            <input
              ref={fileInputRef}
              id={fileInputId}
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={e => onPick(e.target.files?.[0] ?? null)}
              className="sr-only"
            />
            <div className="flex flex-wrap items-center gap-3 rounded-xl border border-dashed border-gray-300 bg-gray-50/80 px-5 py-4">
              <Button
                type="button"
                variant="outline-primary"
                disabled={busy}
                icon={<FileSpreadsheet className="h-4 w-4" />}
                onClick={() => fileInputRef.current?.click()}
              >
                Choose file
              </Button>
              {file ? (
                <div className="min-w-0 flex-1">
                  <p className="m-0 truncate text-sm font-medium text-gray-900">
                    {file.name}
                  </p>
                  {sheetNote && (
                    <p className="m-0 mt-0.5 text-xs text-gray-500">
                      {sheetNote}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>

          <div
            className={`rounded-xl border px-3.5 py-3 ${
              dataset === "synthetic"
                ? "border-amber-200 bg-amber-50"
                : "border-violet-200 bg-violet-50"
            }`}
          >
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Dataset for this file
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              {dataset === "synthetic" ? (
                <FlaskConical className="h-4 w-4 text-amber-700" />
              ) : (
                <Database className="h-4 w-4 text-violet-700" />
              )}
              <span
                className={`text-sm font-semibold ${
                  dataset === "synthetic" ? "text-amber-900" : "text-violet-900"
                }`}
              >
                {DATASET_LABELS[dataset]}
              </span>
            </div>
            <p className="m-0 mt-1.5 text-xs leading-relaxed text-gray-600">
              {dataset === "synthetic"
                ? "Using Synthetic mode from Data Hub. This file stays separate from production reporting."
                : "Using Production mode from Data Hub. This file feeds the live reporting dataset."}
            </p>
          </div>

          <div className="space-y-2">
            <p className="m-0 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              Phases to run
            </p>

            <div className="space-y-2 rounded-xl border border-gray-200 bg-white p-1.5">
              <div className="rounded-lg px-3.5 py-3.5">
                <p className="m-0 text-sm font-medium text-gray-900">
                  Phase 1 — Import + Cleanse + Commit to {datasetLabel}
                </p>
              </div>

              <div className="mx-3.5 border-t border-gray-100" />

              <div className="rounded-lg px-3.5 py-3.5">
                <p className="m-0 text-sm font-medium text-gray-900">
                  Phase 2 — Benchmark → Normalize → Summarize → Category Scores
                  → R&amp;O Scores
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </div>
          )}
        </div>
      )}
    </CommonModal>
  );
}

export default RunWizard;
